import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ReceiptText, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/ui/brand-mark";
import { Section } from "../components/ui/section";
import { useAuth } from "../auth/AuthContext";

export function AboutPage(): ReactNode {
  const { status } = useAuth();
  const home = status === "authenticated" ? "/" : "/welcome";

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <header className="border-b border-outline-variant/40 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to={home} className="flex items-center gap-3">
            <BrandMark />
            <span className="text-headline-md text-primary">Tailspend</span>
          </Link>
          {status === "authenticated" ? (
            <Link to="/">
              <Button variant="ghost" size="sm">
                Back to dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/welcome">
              <Button size="sm">Sign in</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-stack-lg px-6 py-10 sm:px-8">
        <div className="space-y-2">
          <p className="text-label-sm text-on-surface-variant">About</p>
          <h1 className="text-headline-lg">Tailspend</h1>
          <p className="text-body-md text-on-surface-variant">
            Shared spending, without the awkward spreadsheet. Tailspend keeps
            every expense, balance, and contribution clear so your group can
            focus on the plans you made together.
          </p>
        </div>

        <Section
          title="Made for spending together"
          contentClassName="space-y-3 text-body-md"
        >
          <p>
            Create groups for trips, households, or one-off events. Add everyone
            involved, record purchases as they happen, and always know what you
            owe and what you're owed.
          </p>
          <p>
            Split a whole expense or divide individual items equally, by exact
            amounts, percentages, or shares. Multiple people can pay, and
            cross-currency spending stays clear in the group's currency.
          </p>
          <p>
            Plan ahead with recurring expenses, invite people when they're
            ready, and share a read-only group view with anyone who just needs
            to follow along.
          </p>
        </Section>

        <Section
          title="Everything in one place"
          contentClassName="grid gap-4 sm:grid-cols-3"
        >
          <Feature
            icon={<ReceiptText className="size-5" />}
            title="Detailed expenses"
            description="Record who paid and split each item the way that makes sense."
          />
          <Feature
            icon={<Users className="size-5" />}
            title="Flexible groups"
            description="Bring people in, manage access, and share progress without extra accounts."
          />
          <Feature
            icon={<CalendarClock className="size-5" />}
            title="Ongoing costs"
            description="Keep regular household bills and subscriptions from being forgotten."
          />
        </Section>

        <Section title="Privacy">
          <p className="text-body-md text-on-surface-variant">
            Tailspend stores only what's needed to give your group a clear,
            shared record: your profile, groups, expenses, and comments. Sign in
            securely with Google or Apple, and Tailspend never sees your
            password.
          </p>
        </Section>
      </main>
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}): ReactNode {
  return (
    <div className="space-y-2">
      <span className="grid size-10 place-items-center rounded-sm bg-primary-fixed text-primary">
        {icon}
      </span>
      <p className="text-title-md">{title}</p>
      <p className="text-body-sm text-on-surface-variant">{description}</p>
    </div>
  );
}
